import React, { useState, useEffect, useMemo } from 'react';
import { Link } from "react-router-dom";
import 'bootstrap/dist/css/bootstrap.min.css';
import { usePlantData } from '../../../components/data/PlantData';
import { usePortfolioData } from '../../../components/data/PortfolioData';
import SidebarPageLayout from '../../../components/layout/SidebarPageLayout';
import Marquee from '../../../components/layout/Marquee';
import AuxiliaryLoad from '../../../components/graphs/AuxLoad';
import AuxiliaryLoad24HrProfile from '../../../components/graphs/AuxLoad24HrProfile.jsx';
import SolarGenerationVsIrradiance from '../../../components/graphs/SolarGenerationVsIrradiance.jsx';
import SolarPerformancePanel from '../../../components/layout/panels/SolarPerformancePanel.jsx';
import MetricChartPanel from '../../../components/layout/panels/MetricChartPanel.jsx';
import { toSlug } from '../../../util/strings';
import { apiFetch } from '../../../api/apiClient';
import ChartTooltip from '../../../components/layout/ChartTooltip';
import Breadcrumbs from '../../../components/layout/Breadcrumbs';
import { usePortfolioDateFormatter } from '../../../utils/usePortfolioDateFormatter';
import DayRangePicker from '../../../components/layout/DayRangePicker';
import { toInsightsMonthSlug } from '../../../components/graphs/PlantSolarOutputVsIrradiance';
import PerformanceSnapshot from '../../../components/layout/panels/PerformanceSnapshot';

function normalizePortfolioKey(value = '') {
  return String(value).toLowerCase().replace(/[^a-z0-9]/g, '');
}

function recentDayRange(dayCount = 7, today = new Date()) {
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (dayCount - 1));
  return { start, end };
}

function toLocalIsoDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function PerformanceEngineering() {
  const { plant: plantData } = usePlantData();
  const { data: portfolios } = usePortfolioData();
  const portfolioName = toSlug(plantData.plant_name) ?? "";
  const dateFormatter = usePortfolioDateFormatter({ portfolioName });
  const lastUpdated = useMemo(() => {
    const normalized = normalizePortfolioKey(portfolioName);
    const match = (portfolios ?? []).find((node) => (
      normalizePortfolioKey(node.portfolio_name) === normalized
      || normalizePortfolioKey(node.plant_name) === normalized
    ));
    return match?.last_updated ?? null;
  }, [portfolios, portfolioName]);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });
  const [inverterRange, setInverterRange] = useState(() => recentDayRange());
  const [auxRange, setAuxRange] = useState(() => recentDayRange());
  const [dailyAuxRange, setDailyAuxRange] = useState(() => recentDayRange());
  const auxStart = toLocalIsoDate(auxRange?.start);
  const auxEnd = toLocalIsoDate(auxRange?.end);
  const dailyAuxStart = toLocalIsoDate(dailyAuxRange?.start);
  const dailyAuxEnd = toLocalIsoDate(dailyAuxRange?.end);
  const insightsMonth = toInsightsMonthSlug(inverterRange?.end)
    || toInsightsMonthSlug(inverterRange?.start)
    || "june-2026";
  const [powerblocks, setPowerblocks] = useState([]);
  const [selectedPowerblockId, setSelectedPowerblockId] = useState("");

  useEffect(() => {
    document.title = `Vivosol Plant Performance Insights Hub | ${portfolioName} / Insights / Performance Engineering`;
  }, [portfolioName]);

  useEffect(() => {
    let cancelled = false;

    const loadPowerblocks = async () => {
      try {
        const payload = await apiFetch(`/api/devices/portfolio/${portfolioName}/powerblocks`);
        if (cancelled) return;

        const rows = Array.isArray(payload)
          ? payload
          : typeof payload === "string"
            ? JSON.parse(payload)
            : [];

        const options = rows
          .map((row) => ({
            id: row?.id,
            name: row?.name,
          }))
          .filter((row) => row.id !== undefined && row.id !== null && row.name);

        setPowerblocks(options);
        setSelectedPowerblockId((prev) =>
          prev && options.some((option) => String(option.id) === String(prev))
            ? prev
            : (options[0]?.id ?? "")
        );
      } catch (error) {
        console.warn("Powerblocks API fetch failed:", error);
        if (!cancelled) {
          setPowerblocks([]);
          setSelectedPowerblockId("");
        }
      }
    };

    loadPowerblocks();

    return () => {
      cancelled = true;
    };
  }, [portfolioName]);

  return (
    <SidebarPageLayout>
      <div className="content p-5">
        <ChartTooltip tooltip={tooltip} transition={false} />
        <Marquee />
        <div className="d-flex justify-content-between align-items-start gap-3 mt-3">
          <h1 className="mb-0"><Link to={`${plantData.dashboard}`}><strong>{plantData.plant_name}</strong></Link> : {plantData.config} ({plantData.location})</h1>
          {lastUpdated ? (
            <span className="text-nowrap small pt-1" title="KPI last updated">
              <strong>Last Updated:</strong> {dateFormatter.formatUtc(lastUpdated)}
            </span>
          ) : null}
        </div>
        <Breadcrumbs labels={{ portfolioName: plantData.plant_name }} />
        <PerformanceSnapshot />
        <SolarPerformancePanel
          portfolioName={portfolioName}
          range={inverterRange}
          onRangeChange={setInverterRange}
          onTooltip={setTooltip}
          powerblocks={powerblocks}
          selectedPowerblockId={selectedPowerblockId}
          onPowerblockChange={setSelectedPowerblockId}
        />

        <MetricChartPanel
          id="solarGenerationVsIrradiance"
          title="SOLAR GENERATION, MWdc vs SOLAR IRRADIANCE, W/m²"
          dataEndpoint={`/api/graph/portfolio/${portfolioName}/insights/${encodeURIComponent(insightsMonth)}/solar-generation-vs-solar-irradiance`}
          dataMetrics={true}
          onTooltip={setTooltip}
          chart={<SolarGenerationVsIrradiance portfolioName={portfolioName} duration={insightsMonth} />}
        />

        <MetricChartPanel
          id="auxiliaryLoadProfile"
          title="AUXILIARY LOAD PROFILE"
          headerExtra={(
            <DayRangePicker
              range={auxRange}
              setRange={setAuxRange}
              maxRangeDays={null}
              lookbackMonths={6}
            />
          )}
          dataEndpoint={auxStart && auxEnd
            ? `/api/graph/portfolio/${portfolioName}/insights/range/aux-load-24hr?start=${auxStart}&end=${auxEnd}`
            : ""}
          dataMetrics={true}
          onTooltip={setTooltip}
          chart={(
            <AuxiliaryLoad24HrProfile
              portfolioName={portfolioName}
              duration="range"
              startDate={auxStart}
              endDate={auxEnd}
            />
          )}
        />

        <MetricChartPanel
          id="auxiliaryLoad"
          title="AUXILIARY LOAD"
          headerExtra={(
            <DayRangePicker
              range={dailyAuxRange}
              setRange={setDailyAuxRange}
              maxRangeDays={null}
              lookbackMonths={6}
            />
          )}
          dataEndpoint={dailyAuxStart && dailyAuxEnd
            ? `/api/graph/portfolio/${portfolioName}/insights/range/aux-load?start=${dailyAuxStart}&end=${dailyAuxEnd}`
            : ""}
          dataMetrics={true}
          onTooltip={setTooltip}
          chart={(
            <AuxiliaryLoad
              portfolioName={portfolioName}
              duration="range"
              startDate={dailyAuxStart}
              endDate={dailyAuxEnd}
            />
          )}
        />

      </div>
    </SidebarPageLayout>
  );
}

export default PerformanceEngineering;
